import { useQueries, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import {
  assignmentsApi,
  samplesApi,
  type Assignment,
  type Sample,
} from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionButton,
  Panel,
  StatusBadge,
} from '../../components/ui/index.js';
import { ErrorNotice } from '../../components/ErrorNotice.js';
import { formatDate } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { toneForSampleStatus } from '../samples/status.js';
import { testName, toneForAssignmentStatus } from '../work/status.js';
import { SampleTypeName } from '../samples/sample-type-fields.js';

/**
 * What actually arrived against this request, and what the laboratory is doing
 * with it — closing carry-over limitation 41.
 *
 * ===========================================================================
 * WHY THIS REPLACED TWO "NOT IMPLEMENTED" CARDS.
 * ===========================================================================
 * Those cards were honest while the sample register and the work list did not
 * exist. They exist now (Phases 7 and 8), so "this system cannot answer yet"
 * had stopped being true — and a placeholder that is no longer true is simply
 * a wrong statement about the record.
 *
 * WHAT THESE PANELS ARE. Read-only summaries built from the existing sample and
 * assignment endpoints, each linking to the register that owns the record. They
 * add no endpoint, no rule and no state; every count is a count of rows the
 * server actually returned.
 *
 * WHAT THEY NEVER DO. They never claim completeness they cannot support: when
 * the request has more samples than one page holds, the page SAYS how many it
 * is showing out of how many exist, rather than presenting a partial list as
 * the whole. And a caller without the permission sees no section at all rather
 * than an empty one — an empty list would assert that nothing is there.
 */
export function WorkOrderLaboratory({ workOrderId }: { readonly workOrderId: string }) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  const maySeeSamples = can(PERMISSIONS.SAMPLES_VIEW);
  const maySeeWork = can(PERMISSIONS.ASSIGNMENTS_VIEW);

  const samples = useQuery({
    queryKey: ['samples', { workOrderId }],
    queryFn: () => samplesApi.list({ workOrderId, pageSize: 50, includeDisposed: true }),
    enabled: maySeeSamples,
  });

  const rows = samples.data?.data ?? [];
  const total = samples.data?.meta.total ?? rows.length;

  /**
   * The work on each of those samples.
   *
   * One query per sample rather than one filtered query, because an assignment
   * belongs to a SAMPLE and the assignment endpoint filters by that — the work
   * order reaches its laboratory work through the material it delivered, which
   * is also how the laboratory reaches it.
   */
  const assignmentQueries = useQueries({
    queries: rows.map((sample) => ({
      queryKey: ['assignments', { sampleId: sample.id }],
      queryFn: () => assignmentsApi.list({ sampleId: sample.id, pageSize: 100 }),
      enabled: maySeeWork && maySeeSamples,
    })),
  });

  const assignments: { sample: Sample; assignment: Assignment }[] = rows.flatMap(
    (sample, index) =>
      (assignmentQueries[index]?.data?.data ?? []).map((assignment) => ({
        sample,
        assignment,
      })),
  );

  /** A read that failed. It is shown as a failure, never as "none assigned". */
  const assignmentsError =
    assignmentQueries.find((query) => query.error)?.error ?? null;

  if (!maySeeSamples && !maySeeWork) return null;

  const truncated = total > rows.length;

  return (
    <div className="flex min-w-0 flex-col gap-gutter">
      {maySeeSamples ? (
        <Panel
          title={t('workOrders.sectionSamples')}
          description={t('workOrders.samplesHint')}
          actions={
            <ActionButton priority="tertiary" size="sm" onPress={() => navigate('/samples')}>
              {t('workOrders.openSampleRegister')}
            </ActionButton>
          }
        >
          {samples.error ? (
            <ErrorNotice error={samples.error} />
          ) : samples.isLoading ? (
            <p className="text-body text-default-600">{t('common.loading')}</p>
          ) : rows.length === 0 ? (
            <p className="text-body text-default-600">{t('workOrders.samplesNone')}</p>
          ) : (
            <>
              <ul className="-my-2 flex flex-col divide-y divide-default-200">
                {rows.map((sample) => (
                  <SampleLine key={sample.id} sample={sample} />
                ))}
              </ul>
              {truncated ? (
                <p className="mt-stack text-caption text-default-600">
                  {t('workOrders.showingFirst', { shown: rows.length, total })}
                </p>
              ) : null}
            </>
          )}
        </Panel>
      ) : null}

      {maySeeWork ? (
        <Panel
          title={t('workOrders.sectionAssignments')}
          description={t('workOrders.assignmentsHint')}
          actions={
            <ActionButton priority="tertiary" size="sm" onPress={() => navigate('/work')}>
              {t('workOrders.openWorkList')}
            </ActionButton>
          }
        >
          {!maySeeSamples ? (
            // The work is reached through the samples this request delivered,
            // so without the sample register there is nothing honest to list.
            <p className="text-body text-default-600">{t('workOrders.assignmentsNeedSamples')}</p>
          ) : samples.error ? (
            // Nor while the samples could not be read: "no test has been
            // assigned" would then be a statement about rows nobody read.
            <ErrorNotice error={samples.error} />
          ) : samples.isLoading || assignmentQueries.some((query) => query.isLoading) ? (
            <p className="text-body text-default-600">{t('common.loading')}</p>
          ) : assignmentsError ? (
            <ErrorNotice error={assignmentsError} />
          ) : assignments.length === 0 ? (
            <p className="text-body text-default-600">{t('workOrders.assignmentsNone')}</p>
          ) : (
            <>
              <ul className="-my-2 flex flex-col divide-y divide-default-200">
                {assignments.map(({ sample, assignment }) => (
                  <AssignmentLine
                    key={assignment.id}
                    sample={sample}
                    assignment={assignment}
                  />
                ))}
              </ul>
              {truncated ? (
                <p className="mt-stack text-caption text-default-600">
                  {t('workOrders.showingFirst', { shown: rows.length, total })}
                </p>
              ) : null}
            </>
          )}
        </Panel>
      ) : null}

      <p className="text-caption text-default-600">{t('workOrders.laboratoryNotice')}</p>
    </div>
  );
}

function SampleLine({ sample }: { readonly sample: Sample }) {
  const { t } = useTranslation();
  const locale = currentLocale();

  return (
    <li className="flex flex-wrap items-center gap-x-stack gap-y-1 py-2">
      <div className="min-w-0">
        <p className="measure text-body font-semibold" dir="ltr">
          <Link
            to={`/samples/${sample.id}`}
            className="text-primary-700 underline-offset-2 hover:underline"
          >
            {sample.sampleNumber}
          </Link>
        </p>
        <p className="text-caption text-default-600">
          <SampleTypeName code={sample.sampleType} />
          {' · '}
          {t('samples.receivedAt')}
          {': '}
          {formatDate(sample.receivedAt, { locale }) ?? sample.receivedAt}
        </p>
      </div>
      <StatusBadge
        label={t(`status.sample.${sample.status}`)}
        tone={toneForSampleStatus(sample.status)}
        size="sm"
      />
    </li>
  );
}

function AssignmentLine({
  sample,
  assignment,
}: {
  readonly sample: Sample;
  readonly assignment: Assignment;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const locale = currentLocale();

  return (
    <li className="flex flex-wrap items-center gap-x-stack gap-y-1 py-2">
      <div className="min-w-0">
        <p className="text-body font-medium text-default-900">
          {testName(assignment.test, locale)}
        </p>
        <p className="text-caption text-default-600">
          <span dir="ltr" className="measure">
            {sample.sampleNumber}
          </span>
          {' · '}
          {assignment.assignedTo?.displayName ?? t('work.unassigned')}
        </p>
      </div>
      <StatusBadge
        label={t(`status.test.${assignment.status}`)}
        tone={toneForAssignmentStatus(assignment.status)}
        size="sm"
      />
      <ActionButton
        priority="tertiary"
        size="sm"
        className="ms-auto"
        onPress={() => navigate(`/work/${assignment.id}`)}
      >
        {t('common.open')}
      </ActionButton>
    </li>
  );
}
